'use client';

import { useState, useEffect, ReactNode } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from '@/components/ui/carousel/carousel';
import { ArrowHead } from '@/components/Icons/Icons';

interface CarouselWithPaginationProps {
  children: ReactNode;
  className?: string;
  align?: 'start' | 'center' | 'end';
  loop?: boolean;
}

export default function CarouselWithPagination({
  children,
  className = 'w-full',
  align = 'start',
  loop = false,
}: CarouselWithPaginationProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return (
    <Carousel
      setApi={setApi}
      opts={{
        align,
        loop,
      }}
      className={className}
    >
      <CarouselContent>{children}</CarouselContent>

      <div className="flex w-full justify-center items-center gap-0 bg-transparent pt-6">
        {/* Navigation with Pagination */}
        <div className="flex items-center">
          <CarouselPrevious
            aria-label="Previous Slide"
            className="static bg-surface-4 hover:bg-surface-5 inline-flex aspect-square h-12 w-12 rounded-none border-0 p-4 translate-x-0 translate-y-0"
          >
            <ArrowHead className="w-4 text-icon-strong hover:text-icon-strong" />
          </CarouselPrevious>

          {/* Page Numbers */}
          {Array.from({ length: count }, (_, i) => i + 1).map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => api?.scrollTo(pageNum - 1)}
              className={`inline-flex aspect-square h-12 w-12 items-center justify-center cs-button font-medium transition-colors border-0 outline-none ${
                current === pageNum
                  ? 'bg-[var(--Color-Extended-Amethyst-Amethyst-Accent)] text-icon-strong'
                  : 'bg-transparent text-icon-light hover:bg-surface-5 hover:text-icon-strong'
              }`}
              aria-label={`Go to slide ${pageNum}`}
              aria-current={current === pageNum ? 'true' : 'false'}
            >
              {pageNum}
            </button>
          ))}

          <CarouselNext
            aria-label="Next Slide"
            className="static bg-surface-4 hover:bg-surface-5 inline-flex aspect-square h-12 w-12 rounded-none border-0 p-4 translate-x-0 translate-y-0"
          >
            <ArrowHead className="w-4 rotate-180 text-icon-strong" />
          </CarouselNext>
        </div>
      </div>
    </Carousel>
  );
}
